import { useState, useCallback, useRef } from "react";
import messagingService from "../services/messagingService";
import encryptionManager from "../services/encryptionManager";

/**
 * 🎯 useDirectMessageJump Hook
 *
 * Industry-standard implementation for jump-to-message functionality
 * for Direct Messages (1-1 conversations) - WhatsApp/Telegram style.
 *
 * Features:
 * - Jump to any message (search result, quoted message, or pinned)
 * - Load context (25 before + target + 25 after)
 * - Bidirectional infinite scroll from jumped position
 * - Track position to prevent duplicate loading
 * - Smooth scroll and highlighting
 * - Message decryption and formatting
 */
const useDirectMessageJump = (conversationId, formatMessageFn, currentUserId) => {
  // Jump state
  const [jumpedMessages, setJumpedMessages] = useState([]);
  const [targetMessageId, setTargetMessageId] = useState(null);
  const [targetIndex, setTargetIndex] = useState(null);
  const [hasOlder, setHasOlder] = useState(false);
  const [hasNewer, setHasNewer] = useState(false);
  const [cursors, setCursors] = useState({ older: null, newer: null });
  const [isJumpedMode, setIsJumpedMode] = useState(false);
  const [isJumping, setIsJumping] = useState(false);
  const [isLoadingOlder, setIsLoadingOlder] = useState(false);
  const [isLoadingNewer, setIsLoadingNewer] = useState(false);

  // Loading refs to prevent duplicate requests
  const loadingRef = useRef({ older: false, newer: false });

  /**
   * Process and format messages (decrypt, format sender info, etc.)
   */
  const processMessages = useCallback(
    async (messages) => {
      if (!messages || !Array.isArray(messages)) return [];

      const processed = await Promise.all(
        messages.map(async (msg) => {
          try {
            // Decrypt if encrypted
            let decryptedMsg = msg;
            if (msg.isEncrypted && msg.encryptedContent && conversationId) {
              try {
                decryptedMsg = await encryptionManager.processReceivedMessage(
                  msg,
                  conversationId
                );
              } catch (error) {
decryptedMsg = {
                  ...msg,
                  content: "⚠️ [Failed to decrypt]",
                  error: true,
                };
              }
            }

            // Format message if formatter provided
            if (formatMessageFn && typeof formatMessageFn === "function") {
              return formatMessageFn(decryptedMsg);
            }

            // Default formatting
            const isMine =
              currentUserId &&
              (decryptedMsg.sender?._id === currentUserId ||
                decryptedMsg.sender?.id === currentUserId ||
                decryptedMsg.sender === currentUserId);

            return {
              ...decryptedMsg,
              senderId: isMine ? "me" : decryptedMsg.sender?._id || decryptedMsg.sender,
              serverId: decryptedMsg.serverId || decryptedMsg._id,
              status: {
                sending: false,
                sent: true,
                delivered: decryptedMsg.status?.delivered || false,
                read: decryptedMsg.status?.read || false,
                failed: false,
              },
              type: decryptedMsg.messageType || decryptedMsg.type || "text",
            };
          } catch (error) {
return msg;
          }
        })
      );

      return processed;
    },
    [conversationId, formatMessageFn, currentUserId]
  );

  /**
   * Jump to a specific message (search result, quoted, or pinned)
   * Loads the target message with 25 messages before and after
   */
  const jumpToMessage = useCallback(
    async (messageId) => {
      if (!conversationId || !messageId) return null;

      setIsJumping(true);
      try {
const response = await messagingService.getMessageContext(
          conversationId,
          messageId,
          25,
          25
        );

        if (response.success) {
          const { messages, target, pagination } = response.data;
// 🔧 CRITICAL: Process and format messages before setting state
          const processedMessages = await processMessages(messages);
setJumpedMessages(processedMessages);
          setTargetMessageId(messageId);
          setTargetIndex(target.index);
          setHasOlder(pagination.hasOlder);
          setHasNewer(pagination.hasNewer);
          setCursors(pagination.cursors || { older: null, newer: null });
          setIsJumpedMode(true);

          return target.index; // Return index for scrolling
        }
      } catch (error) {
return null;
      } finally {
        setIsJumping(false);
      }

      return null;
    },
    [conversationId, processMessages]
  );

  /**
   * Load older messages (scroll up in jumped mode)
   */
  const loadOlder = useCallback(async () => {
    if (loadingRef.current.older || !hasOlder || !cursors.older) {
return null;
    }

    loadingRef.current.older = true;
    setIsLoadingOlder(true);
try {
      const response = await messagingService.loadMoreMessages(
        conversationId,
        cursors.older,
        "older",
        50
      );

      if (response.success) {
        const { messages, pagination } = response.data;
// 🔧 CRITICAL: Process and format messages
        const processedMessages = await processMessages(messages);

        setJumpedMessages((prev) => {
          // Deduplicate
          const combined = [...processedMessages, ...prev];
          const unique = combined.filter(
            (msg, index, self) =>
              index === self.findIndex((m) => m._id === msg._id)
          );
          return unique;
        });

        // Adjust target index since we prepended messages
        setTargetIndex((prev) => (prev !== null ? prev + processedMessages.length : null));

        setHasOlder(pagination.hasMore);
        setCursors((prev) => ({
          ...prev,
          older: pagination.nextCursor || null,
        }));

        return processedMessages.length; // Return count for scroll adjustment
      }
    } catch (error) { /* ignored */ } finally {
      loadingRef.current.older = false;
      setIsLoadingOlder(false);
    }

    return null;
  }, [conversationId, hasOlder, cursors.older, processMessages]);

  /**
   * Load newer messages (scroll down in jumped mode)
   */
  const loadNewer = useCallback(async () => {
    if (loadingRef.current.newer || !hasNewer || !cursors.newer) {
return null;
    }

    loadingRef.current.newer = true;
    setIsLoadingNewer(true);
try {
      const response = await messagingService.loadMoreMessages(
        conversationId,
        cursors.newer,
        "newer",
        50
      );

      if (response.success) {
        const { messages, pagination } = response.data;
// 🔧 CRITICAL: Process and format messages
        const processedMessages = await processMessages(messages);

        setJumpedMessages((prev) => {
          // Deduplicate
          const combined = [...prev, ...processedMessages];
          const unique = combined.filter(
            (msg, index, self) =>
              index === self.findIndex((m) => m._id === msg._id)
          );
          return unique;
        });

        setHasNewer(pagination.hasMore);
        setCursors((prev) => ({
          ...prev,
          newer: pagination.nextCursor || null,
        }));

        return processedMessages.length;
      }
    } catch (error) { /* ignored */ } finally {
      loadingRef.current.newer = false;
      setIsLoadingNewer(false);
    }

    return null;
  }, [conversationId, hasNewer, cursors.newer, processMessages]);

  /**
   * Exit jumped mode and return to normal chat view (latest messages)
   */
  const exitJumpedMode = useCallback(() => {
setJumpedMessages([]);
    setTargetMessageId(null);
    setTargetIndex(null);
    setHasOlder(false);
    setHasNewer(false);
    setCursors({ older: null, newer: null });
    setIsJumpedMode(false);
  }, []);

  /**
   * Clear highlight after animation
   */
  const clearHighlight = useCallback(() => {
    setTargetMessageId(null);
    setTargetIndex(null);
  }, []);

  return {
    // State
    jumpedMessages,
    targetMessageId,
    targetIndex,
    hasOlder,
    hasNewer,
    isJumpedMode,
    isJumping,
    isLoadingOlder,
    isLoadingNewer,
    cursors,

    // Actions
    jumpToMessage,
    loadOlder,
    loadNewer,
    exitJumpedMode,
    clearHighlight,
  };
};

export default useDirectMessageJump;
